import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  PaginatedGroupsDocument,
  PaginatedGroupsQuery,
  PaginatedGroupsQueryVariables,
} from 'core/graphql';

/**
 * Пауза после последнего набранного символа, прежде чем искать группу:
 * без неё `PaginatedGroups` уходил на каждую букву.
 */
export const GROUP_SEARCH_DEBOUNCE_MS = 300;

/** Вариант выпадашки выбора группы (react-select). */
export interface IGroupPickerOption {
  value: string;
  label: string;
  currency?: string | null;
  /** Дата старта группы (ISO) — вторая строка подписи варианта. */
  startDate?: string | null;
}

interface IUseGroupOptionsLoader {
  loadOptions: (input: string) => Promise<IGroupPickerOption[]>;
  /**
   * Последний поиск упал. Отдельно от пустой выдачи: «ничего не нашлось» на
   * сетевой ошибке отправляло бы оператора искать другую группу, хотя группа
   * есть — не загрузился список.
   */
  loadFailed: boolean;
}

/**
 * Поиск группы для выпадашки «в какую группу» — один источник на перевод
 * (`MoveToGroupInlinePopup`) и «Исправить группу» (ERP-110): тот же запрос
 * `PaginatedGroups`, те же 20 первых совпадений, та же валюта у варианта.
 *
 * `no-cache`: выдача поиска — одноразовая, в кеше она только копила бы
 * страницы `allGroups` под каждую набранную букву.
 *
 * Дебаунс `GROUP_SEARCH_DEBOUNCE_MS`: запрос уходит, когда набор затих.
 * Перебитый вызов сразу получает `[]` — react-select такой ответ отбрасывает
 * (и не кеширует: сверка с последним запросом у него раньше записи в
 * `cacheOptions`), так что ни одно обещание не висит.
 */
export const useGroupOptionsLoader = (): IUseGroupOptionsLoader => {
  const apollo = useApolloClient();
  const [loadFailed, setLoadFailed] = useState(false);
  // Поиск может вернуться после закрытия окна — состояние тогда не трогаем.
  const alive = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<((options: IGroupPickerOption[]) => void) | null>(
    null,
  );
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
      pending.current?.([]);
      pending.current = null;
    };
  }, []);

  const fetchOptions = useCallback(
    async (input: string): Promise<IGroupPickerOption[]> => {
      try {
        const result = await apollo.query<
          PaginatedGroupsQuery,
          PaginatedGroupsQueryVariables
        >({
          query: PaginatedGroupsDocument,
          variables: { query: input || '', first: 20 },
          fetchPolicy: 'no-cache',
        });
        if (alive.current) setLoadFailed(false);
        const edges = result.data?.allGroups?.edges || [];
        return edges
          .map((edge) => edge?.node)
          .filter((node): node is NonNullable<typeof node> => Boolean(node))
          .map((node) => ({
            value: node.id,
            label: node.name,
            currency: node.currency,
            startDate: node.startDate ?? null,
          }));
      } catch {
        if (alive.current) setLoadFailed(true);
        return [];
      }
    },
    [apollo],
  );

  const loadOptions = useCallback(
    (input: string): Promise<IGroupPickerOption[]> =>
      new Promise<IGroupPickerOption[]>((resolve) => {
        if (timer.current) clearTimeout(timer.current);
        pending.current?.([]);
        pending.current = resolve;
        timer.current = setTimeout(() => {
          timer.current = null;
          pending.current = null;
          fetchOptions(input).then(resolve);
        }, GROUP_SEARCH_DEBOUNCE_MS);
      }),
    [fetchOptions],
  );

  return { loadOptions, loadFailed };
};
